import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, Lock, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const PAY_TIDS = {
    dialog: "family-payment-dialog",
    submitBtn: "family-payment-submit-btn",
    cancelBtn: "family-payment-cancel-btn",
    liveBadge: "family-payment-live-badge",
};

const PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = PK ? loadStripe(PK) : null;
// Detect LIVE vs test key at load
const IS_LIVE = PK.startsWith("pk_live_");

function CardCollectForm({ onSuccess, onClose }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    async function submit(e) {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setError("");

        const { error: subErr, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                // No return URL — we handle success in-place.
                return_url: window.location.origin + "/dashboard",
            },
            redirect: "if_required",
        });

        if (subErr) {
            setError(subErr.message || "Card was declined.");
            setSubmitting(false);
            return;
        }
        if (setupIntent?.status === "succeeded") {
            // Persist "on file" flag on backend
            try {
                await api.post("/stripe/family/confirm-payment-method", {
                    payment_method: setupIntent.payment_method,
                    setup_intent: setupIntent.id,
                });
            } catch (err) {
                // Non-fatal — card is attached to the customer already
            }
            toast.success("Card saved — you're ready to book care");
            onSuccess?.();
        } else {
            setError("Setup did not complete. Please try again.");
        }
        setSubmitting(false);
    }

    return (
        <form onSubmit={submit} className="space-y-5">
            <PaymentElement
                options={{
                    layout: { type: "tabs", defaultCollapsed: false },
                    fields: { billingDetails: { name: "auto", email: "auto" } },
                }}
            />
            {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 font-dmsans">
                    {error}
                </div>
            )}
            <div className="flex items-center justify-between gap-3 pt-2">
                <div className="text-xs text-stone-500 font-dmsans flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Card is processed by Stripe. We never see or store it.
                </div>
                <div className="flex gap-2">
                    <Button
                        data-testid={PAY_TIDS.cancelBtn}
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        disabled={submitting}
                        className="rounded-full"
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid={PAY_TIDS.submitBtn}
                        type="submit"
                        disabled={!stripe || !elements || submitting}
                        className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
                    >
                        {submitting ? "Saving…" : "Save card"}
                    </Button>
                </div>
            </div>
        </form>
    );
}

export default function FamilyPaymentDialog({ open, onOpenChange, onSuccess }) {
    const [clientSecret, setClientSecret] = useState("");
    const [loading, setLoading] = useState(false);
    const [mocked, setMocked] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!open) return;
        setErr("");
        setClientSecret("");
        setMocked(false);
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.post("/stripe/family/setup-payment");
                if (data.mocked) {
                    setMocked(true);
                    toast.success("Payment method saved (dev mode)");
                    onSuccess?.();
                    onOpenChange(false);
                } else {
                    setClientSecret(data.client_secret);
                }
            } catch (e) {
                setErr(e?.response?.data?.detail || "Could not start card setup");
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    const options = useMemo(() => (clientSecret ? {
        clientSecret,
        appearance: {
            theme: "stripe",
            variables: {
                colorPrimary: "#047857",  // emerald-700
                colorText: "#1c1917",
                colorBackground: "#ffffff",
                fontFamily: "'DM Sans', system-ui, sans-serif",
                borderRadius: "10px",
            },
        },
    } : null), [clientSecret]);

    if (mocked) return null; // resolved immediately; dialog already closed

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={PAY_TIDS.dialog}
                className="max-w-md rounded-2xl"
            >
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-emerald-700" />
                        <DialogTitle className="font-outfit">Add payment method</DialogTitle>
                    </div>
                    <DialogDescription className="font-dmsans text-stone-500">
                        Card is saved securely to Stripe. Nothing is charged until a caregiver is confirmed for your care request.
                    </DialogDescription>
                    {IS_LIVE && clientSecret && (
                        <div
                            data-testid={PAY_TIDS.liveBadge}
                            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-50 border border-red-200 text-[10px] font-outfit font-bold uppercase tracking-[0.2em] text-red-700 w-fit"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                            Stripe live mode
                        </div>
                    )}
                </DialogHeader>

                {loading && (
                    <div className="py-10 text-center text-sm text-stone-500 font-dmsans">
                        Preparing secure card entry…
                    </div>
                )}
                {err && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 font-dmsans">
                        {err}
                    </div>
                )}
                {!stripePromise && (
                    <div className="text-sm text-orange-700 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 font-dmsans">
                        Card entry is unavailable — publishable key not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY.
                    </div>
                )}
                {stripePromise && clientSecret && (
                    <Elements stripe={stripePromise} options={options}>
                        <CardCollectForm
                            onSuccess={() => { onSuccess?.(); onOpenChange(false); }}
                            onClose={() => onOpenChange(false)}
                        />
                    </Elements>
                )}
            </DialogContent>
        </Dialog>
    );
}
